import { z } from 'zod';
import * as React from 'react';
import {
  CheckCircle2,
  ExternalLink,
  KeyRound,
  RefreshCw,
  Unplug,
  XCircle,
} from 'lucide-react';
import { Alert } from './Alert';
import { Button } from './Button';
import { Card, CardBody, CardHeader, Surface } from './Card';
import { Form, FormField, type ZodForm } from './Form';
import { Input } from './Input';
import { Icon } from './Icon';
import { Divider, Grid, Inline, Stack } from './Layout';
import { List, ListItem } from './List';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogTitle,
} from './Sheet';
import {
  ROADSYNC_LOCATION_CANT_INVOICE,
  RoadSyncLocationRadios,
  isRoadSyncLocationSelectable,
  type RoadSyncLocationOption,
} from './RoadSyncConnectForm';
import { Loading } from './Spinner';
import { Text } from './Text';

export interface RoadSyncLocationPickerProps {
  /** The RoadSync locations the refresh turned up. */
  choices: RoadSyncLocationOption[];
  selectedId: string;
  onSelect: (id: string) => void;
  onSave: () => void;
  saving?: boolean;
}

/**
 * The choose-a-location panel that appears under the RoadSync options when a
 * refresh finds more than one location: one radio per location and Save.
 * A location that can't take invoices is locked with its reason; when none
 * can, the panel says so and offers no Save.
 */
export function RoadSyncLocationPicker({
  choices,
  selectedId,
  onSelect,
  onSave,
  saving = false,
}: RoadSyncLocationPickerProps) {
  const anySelectable = choices.some(isRoadSyncLocationSelectable);
  return (
    <Surface bordered radius="md" padding="sm" tone="none">
      <Stack gap={3}>
        <Stack gap={1}>
          <Text variant="body-sm-strong">Choose a location</Text>
          <Text variant="caption" tone="muted">
            Payments will be created at this RoadSync location.
          </Text>
        </Stack>
        {!anySelectable && (
          <Alert tone="warning">{ROADSYNC_LOCATION_CANT_INVOICE}</Alert>
        )}
        <RoadSyncLocationRadios
          locations={choices}
          value={selectedId}
          onValueChange={onSelect}
          disabled={saving}
        />
        {anySelectable && (
          <Button size="sm" onClick={onSave} disabled={!selectedId || saving}>
            {saving ? 'Saving...' : 'Save location'}
          </Button>
        )}
      </Stack>
    </Surface>
  );
}

export const roadSyncApiKeySchema = z.object({
  apiKey: z.string().trim().min(1, 'API Key is required'),
});

export interface RoadSyncApiKeyPanelProps {
  hasApiKey?: boolean;
  hasMultilineProduct?: boolean;
  /** The update form is showing under the status line. */
  formOpen: boolean;
  onToggleForm: () => void;
  /** From `useZodForm(roadSyncApiKeySchema, …)`. */
  form: ZodForm<typeof roadSyncApiKeySchema>;
  onSave: (values: { apiKey: string }) => void;
  onCancel: () => void;
  saving?: boolean;
}

function StatusMark({
  ok,
  yes,
  no,
}: {
  ok: boolean | undefined;
  yes: string;
  no: string;
}) {
  return ok ? (
    <Inline as="span" gap={1} align="center">
      <Icon icon={CheckCircle2} size="sm" tone="success" />
      <Text as="span" variant="caption" tone="success">
        {yes}
      </Text>
    </Inline>
  ) : (
    <Inline as="span" gap={1} align="center">
      <Icon icon={XCircle} size="sm" tone="destructive" />
      <Text as="span" variant="caption" tone="destructive">
        {no}
      </Text>
    </Inline>
  );
}

/**
 * The API key block of the RoadSync options: whether a key is configured
 * and multi-line invoicing is enabled, with the add/update form folded under
 * a button. Enter in the field saves, the same as the Save button.
 */
export function RoadSyncApiKeyPanel({
  hasApiKey,
  hasMultilineProduct,
  formOpen,
  onToggleForm,
  form,
  onSave,
  onCancel,
  saving = false,
}: RoadSyncApiKeyPanelProps) {
  return (
    <Stack gap={3}>
      <Inline gap={0} align="center" justify="between">
        <Inline gap={2} align="center">
          <Icon icon={KeyRound} size="sm" tone="muted" />
          <Text as="span" variant="body-sm-strong">
            API Key
          </Text>
          <StatusMark ok={hasApiKey} yes="Configured" no="Not configured" />
        </Inline>
        <Button variant="outline" size="sm" onClick={onToggleForm}>
          {hasApiKey ? 'Update API Key' : 'Add API Key'}
        </Button>
      </Inline>

      <Inline gap={2} align="center">
        <Text as="span" variant="body-sm-strong">
          Multi-line invoicing
        </Text>
        <StatusMark ok={hasMultilineProduct} yes="Enabled" no="Not enabled" />
      </Inline>
      {!hasMultilineProduct && (
        <Text variant="caption" tone="muted">
          Ask RoadSync support to enable multi-line invoicing so Relay can send
          itemized charges.
        </Text>
      )}

      {formOpen && (
        <Surface radius="md" padding="sm" tone="none">
          <Form form={form} gap={3} onSubmit={onSave}>
            <FormField
              form={form}
              name="apiKey"
              label="New API Key"
              description={
                <>Found in your RoadSync dashboard under Settings &rarr; API</>
              }
            >
              {(field) => (
                <Input
                  type="password"
                  placeholder="Enter your RoadSync API Key"
                  {...field}
                  disabled={saving}
                />
              )}
            </FormField>
            <Inline gap={2} align="stretch" justify="end">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={onCancel}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button type="submit" size="sm" disabled={saving}>
                {saving ? 'Saving...' : 'Save'}
              </Button>
            </Inline>
          </Form>
        </Surface>
      )}
    </Stack>
  );
}

export interface RoadSyncOptionsViewProps {
  /** The connection is still being looked up: a spinner in place of the card body. */
  loading?: boolean;
  /** Where "RoadSync Dashboard" goes — the location's admin centre when known. */
  dashboardUrl: string;
  onRefreshSetup: () => void;
  refreshing?: boolean;
  /**
   * The API key is saved but no location is chosen, so RoadSync can't take
   * payments yet. Shows a prompt whose button runs `onRefreshSetup`.
   */
  needsLocation?: boolean;
  /** Present while a refresh is waiting on the owner to pick a location. */
  locationPicker?: RoadSyncLocationPickerProps;
  apiKey: RoadSyncApiKeyPanelProps;
  /** Whether the "are you sure?" is showing. The screen owns it so it can
   * stay open while the request runs and close when it succeeds. */
  confirmOpen: boolean;
  onConfirmOpenChange: (open: boolean) => void;
  onDisconnect: () => void;
  disconnecting?: boolean;
}

/**
 * The RoadSync integration's options card: the dashboard link and setup
 * refresh, the location picker when one is needed, the API key panel, and
 * Disconnect with a confirmation in front of it.
 *
 *   <RoadSyncOptionsView
 *     dashboardUrl={url}
 *     onRefreshSetup={syncLocation}
 *     apiKey={{ hasApiKey, formOpen, onToggleForm, apiKey, onApiKeyChange, onSave, onCancel }}
 *     confirmOpen={confirming}
 *     onConfirmOpenChange={setConfirming}
 *     onDisconnect={disconnect}
 *   />
 */
export function RoadSyncOptionsView({
  loading = false,
  dashboardUrl,
  onRefreshSetup,
  refreshing = false,
  needsLocation = false,
  locationPicker,
  apiKey,
  confirmOpen,
  onConfirmOpenChange,
  onDisconnect,
  disconnecting = false,
}: RoadSyncOptionsViewProps) {
  const header = (
    <CardHeader
      title="Options"
      description="Integration settings and actions"
    />
  );

  if (loading) {
    return (
      <Card>
        {header}
        <CardBody>
          <Loading title="Loading options..." pad="sm" />
        </CardBody>
      </Card>
    );
  }

  return (
    <>
      <Card>
        {header}
        <CardBody>
          <Stack gap={4}>
            <Stack gap={3}>
              <Text variant="body-sm" tone="muted">
                Visit your RoadSync dashboard to configure:
              </Text>
              <Grid cols={{ base: 1, md: 3 }} gap={4} align="stretch">
                <Grid cols={2} gap={6} align="start" className="md:min-w-0">
                  <List tone="muted" gap={1}>
                    <ListItem>Locations</ListItem>
                    <ListItem>Invoices</ListItem>
                    <ListItem>Work orders</ListItem>
                  </List>
                  <List tone="muted" gap={1}>
                    <ListItem>Taxes</ListItem>
                    <ListItem>And more</ListItem>
                  </List>
                </Grid>
                <Inline
                  gap={0}
                  align="stretch"
                  justify="end"
                  className="h-full min-h-0"
                >
                  <Button
                    variant="primary"
                    className="h-full min-h-9 w-full md:w-auto md:min-w-[12rem]"
                    href={dashboardUrl}
                    external
                    leadingIcon={<ExternalLink />}
                  >
                    RoadSync Dashboard
                  </Button>
                </Inline>
                <Inline
                  gap={0}
                  align="stretch"
                  justify="end"
                  className="h-full min-h-0"
                >
                  <Button
                    variant="outline"
                    onClick={onRefreshSetup}
                    disabled={refreshing}
                    className="h-full min-h-9 w-full md:w-auto md:min-w-[10rem]"
                    leadingIcon={<RefreshCw />}
                  >
                    {refreshing ? 'Refreshing...' : 'Refresh RoadSync Setup'}
                  </Button>
                </Inline>
              </Grid>
            </Stack>

            {needsLocation &&
              !(locationPicker && locationPicker.choices.length > 0) && (
                <Alert
                  tone="warning"
                  title="Pick a RoadSync location to start taking payments"
                  actions={
                    <Button
                      size="sm"
                      onClick={onRefreshSetup}
                      loading={refreshing}
                      disabled={refreshing}
                    >
                      Choose a location
                    </Button>
                  }
                >
                  Your API key is saved. Choose which RoadSync location gets the
                  payments.
                </Alert>
              )}

            {locationPicker && locationPicker.choices.length > 0 && (
              <RoadSyncLocationPicker {...locationPicker} />
            )}

            <Divider />

            <RoadSyncApiKeyPanel {...apiKey} />

            <Divider />

            <Inline gap={0}>
              <Button
                variant="destructive"
                onClick={() => onConfirmOpenChange(true)}
                disabled={disconnecting}
                leadingIcon={<Unplug />}
              >
                {disconnecting ? 'Disconnecting...' : 'Disconnect RoadSync'}
              </Button>
            </Inline>
          </Stack>
        </CardBody>
      </Card>

      <AlertDialog open={confirmOpen} onOpenChange={onConfirmOpenChange}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Disconnect RoadSync?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to disconnect RoadSync? This will remove the
              integration and you will need to reconnect to restore access.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Inline gap={3} align="stretch" justify="end">
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={onDisconnect}
              disabled={disconnecting}
              destructive
            >
              {disconnecting ? 'Disconnecting...' : 'Disconnect'}
            </AlertDialogAction>
          </Inline>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
